import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PageSpinner } from '../components/Spinner';
import { ClanPublicPageView } from '../components/ClanPublicPageView';

// ──────────────────────────────────────────────────────────────────────────────
// ClanHome — org-scoped public clan profile page.
// Routes: /team/:name (public, no AuthGate) and /clan-page/:name (in-shell).
//
// Backend: GET /portal/public/clan/:name — gated on the ordinary org-scoped
// visibility boundary (myVisibleOrgIds), NOT a live Discord-guild-membership
// check (that's the stronger gate /public/org/:slug uses). Status map:
// 401 = not logged in, 403 = clan's org not in the viewer's visible set,
// 404 = missing clan / HTML-body (CloudFront SPA fallback).
//
// The actual rendered page (hero, stats, leadership, teams, roster, comp
// history, apply flow) lives in ClanPublicPageView — a pure presentational
// component. This file owns only: data fetching and the loading/error guards.
// No accent side-effect here — unlike OrgHome, ClanPublicPageResponse carries
// no accentColor field to apply.
// ──────────────────────────────────────────────────────────────────────────────

export function ClanHome() {
  // ALL hooks must be declared before any early return.
  const { name } = useParams<{ name: string }>();
  const clanName = name ? decodeURIComponent(name) : undefined;

  const { data, isLoading, error } = useQuery({
    queryKey: ['publicClanPage', clanName],
    queryFn: () => api.publicClanPage(clanName!),
    enabled: !!clanName,
    retry: false,
  });

  // ── Early returns (all hooks above this line) ─────────────────────────────

  if (!clanName) return null;

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <PageSpinner />
      </div>
    );
  }

  if (error || !data) {
    const msg = (error as Error | null)?.message ?? '';
    if (msg === 'unauthorized') {
      return (
        <div className="flex min-h-screen items-center justify-center p-6">
          <div className="max-w-sm text-center">
            <h2 className="mb-2 text-lg font-semibold text-white">Sign in to view this page</h2>
            <p className="text-sm text-gray-400">
              This page is only visible to signed-in members of the clan's organisation. Open the GG
              Anvil portal and sign in (run <code className="font-mono">/ggportal</code> in your Discord
              server to get a link), then reload.
            </p>
          </div>
        </div>
      );
    }
    if (msg === 'forbidden') {
      return (
        <div className="flex min-h-screen items-center justify-center p-6">
          <div className="max-w-sm text-center">
            <h2 className="mb-2 text-lg font-semibold text-white">Not accessible</h2>
            <p className="text-sm text-gray-400">
              You need to be a member of this clan's organisation to view this page.
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <p className="max-w-sm text-center text-gray-400">
          {msg.includes('not_found')
            ? "This clan doesn't have a page here."
            : 'Something went wrong loading this page.'}
        </p>
      </div>
    );
  }

  return <ClanPublicPageView data={data} />;
}
